import { addIndex, compose, defaultTo, filter, flatten, forEach, inc, isNil, length, map, pathOr, propEq, propOr } from 'ramda';
import { BLOCK_KEYS } from '../../shared/documentHistoriesConstants';

const forEachIndexed = addIndex(forEach);

// rowIndex and widgetIndex are deliberately excluded: they shift as soon as a row
// is inserted or removed above, which would turn a genuine deletion into an
// unrecorded one. The baseline therefore counts occurrences per section+block
// rather than tracking positions.
export const baselineKey = (widgetId, sectionIndex, block) => `${widgetId}|${sectionIndex}|${block}`;

// How many times widgetId sits in one section+block of `pages`.
export const countWidgetOccurrences = (pages = [], widgetId, sectionIndex, block) => compose(
  length,
  filter(propEq(widgetId, 'widgetId')),
  flatten,
  map(propOr([], 'widgets')),
  pathOr([], [sectionIndex, block])
)(pages);

// Occurrence count of every widget placement held by the persisted document at
// the start of an edit session. Comparing it with the count left after a removal
// tells a genuine deletion from the removal of a widget added in-session.
export const buildWidgetBaseline = (pages = []) => {
  const baseline = new Map();
  forEachIndexed((section, sectionIndex) => {
    forEach((blockKey) => {
      forEach((row) => {
        forEach((widget) => {
          const widgetId = propOr(null, 'widgetId', widget);
          if (isNil(widgetId)) return;
          const key = baselineKey(widgetId, sectionIndex, blockKey);
          baseline.set(key, inc(defaultTo(0, baseline.get(key))));
        }, propOr([], 'widgets', row));
      }, propOr([], blockKey, section));
    }, BLOCK_KEYS);
  }, pages);
  return baseline;
};

// Fail-open on an *uninitialised* baseline only: Infinity makes every deletion
// look like the loss of a persisted occurrence, and an extra entry is preferable
// to a genuine deletion lost. An empty Map is a legitimate baseline — a document
// opened with no widget placed — and yields 0.
export const baselineOccurrences = (baseline, widgetId, sectionIndex, block) => {
  if (isNil(baseline)) return Infinity;
  return defaultTo(0, baseline.get(baselineKey(widgetId, sectionIndex, block)));
};
